// client/src/components/plataforma/users/UserManagementDrawer.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../../../services/api';
import {
    User, Lock,
    Loader2, AlertCircle, XCircle
} from 'lucide-react';
import PageHeader from '../../layout/PageHeader';

import useUser, { useMutateUsers, useUserById } from '../../../hooks/useUsers';
import { useAccountUsersByUser } from '../../../hooks/useRelationships';
import { useRelationshipsMutations } from '../../../hooks/useRelationships';
import useAlert from '../../../hooks/useAlert';
import { useAuth } from '../../../context/auth/AuthProvider';
import { isMatrizAdmin, isFranquiaAdmin, isContaAdmin } from '../../../utils/permissions';

import UserProfileTab from './tabs/UserProfileTab';
import PermissoesUsuarioTab from './tabs/PermissoesUsuarioTab';
import UserPasswordTab from './tabs/UserPasswordTab';
import { FormStepper, FormStepNavigation } from '../../shared/FormStepper';

const STEP_LABELS = ['Perfil', 'Permissões', 'Segurança'];

const safeId = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (data._id) return String(data._id);
    if (data.id) return String(data.id);
    return String(data) || '';
};

const UserManagementDrawer = ({ contextData }) => {
    // ✅ CORREÇÃO: as rotas que montam este componente carregam o tenant na
    // própria URL (/contas/:contaId/equipe/:userId/editar e
    // /franquias/:franquiaId/admins/:userId/editar). Sem ler esses params
    // aqui, o contexto só chegava via location.state — que some num F5 —
    // então a aba "Permissões" ficava sem contaId/franquiaId e o campo
    // "Papel Principal (Role)" não carregava a role do usuário.
    const { userId, contaId: routeContaId, franquiaId: routeFranquiaId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { setAlert } = useAlert();
    const { user: currentUser } = useAuth();
    
    const { createUser, updateUser, isPending: loading } = useMutateUsers();
    const { createAccountUser } = useRelationshipsMutations();
    
    const prevLocationRef = useRef(location.pathname);
    const prevUserIdRef = useRef(userId);
    const isInitializedRef = useRef(false);
    const mountedRef = useRef(true);
    
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState(null);
    const [mode, setMode] = useState(null);
    const [loadingError, setLoadingError] = useState(null);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            isInitializedRef.current = false;
        };
    }, []);

    const mergedContextData = useMemo(() => {
        const merged = {
            ...contextData,
            ...(location.state || {})
        };
        // Params da rota como fallback — garantem o tenant mesmo sem
        // location.state (acesso direto por URL, reload, link externo).
        if (!merged.contaId && routeContaId) merged.contaId = routeContaId;
        if (!merged.franquiaId && routeFranquiaId) merged.franquiaId = routeFranquiaId;
        return merged;
    }, [contextData, location.state, routeContaId, routeFranquiaId]);

    const targetAccountId = useMemo(() => safeId(mergedContextData?.contaId), [mergedContextData?.contaId]);
    const targetFranquiaId = useMemo(() => safeId(mergedContextData?.franquiaId), [mergedContextData?.franquiaId]);
    const isFranquiaMode = useMemo(() => !!targetFranquiaId, [targetFranquiaId]);

    useEffect(() => {
        if (!mountedRef.current) return;
        
        if (mode && location.pathname === prevLocationRef.current && userId === prevUserIdRef.current) {
            return;
        }

        let newMode = null;
        
        if (location.state?.isCreatingMode) {
            newMode = 'creating';
        }
        else if (!userId || userId === 'novo') {
            newMode = 'creating';
        }
        else {
            newMode = 'editing';
        }

        if (newMode !== mode) {
            setMode(newMode);
            setLoadingError(null);
            setFormData(null);
            isInitializedRef.current = false;
        }

        prevLocationRef.current = location.pathname;
        prevUserIdRef.current = userId;
    }, [userId, location.state?.isCreatingMode, location.pathname, mode]);

    const shouldFetchUser = useMemo(() => 
        mode === 'editing' && userId && userId !== 'novo', 
    [mode, userId]);
    
    const targetUserId = useMemo(() => 
        shouldFetchUser ? safeId(userId) : '', 
    [shouldFetchUser, userId]);

    const { 
        data: userData, 
        isLoading: isLoadingUser, 
        refetch: refetchUser,
        error: userError
    } = useUserById(targetUserId, {
        enabled: shouldFetchUser && mountedRef.current,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false
    });

    const { 
        data: accountUsersData,
        isLoading: isLoadingAccountUser,
        refetch: refetchAccountUser
    } = useAccountUsersByUser(targetUserId, {
        enabled: shouldFetchUser && mountedRef.current
    });

    const isOwnUser = useMemo(() => {
        const authUserId = currentUser?._id || currentUser?.id;
        const targetId = formData?._id || targetUserId;
        
        if (!authUserId || !targetId) {
            return false;
        }
        
        return String(authUserId) === String(targetId);
    }, [currentUser, formData, targetUserId]);

    const canEdit = useMemo(() => {
        if (isMatrizAdmin(currentUser)) return true;
        if (isFranquiaAdmin(currentUser)) return true;
        if (isContaAdmin(currentUser)) return true;
        return isOwnUser;
    }, [currentUser, isOwnUser]);

    const initializeCreationForm = useCallback(() => {
        return {
            _id: 'new-user-temp-' + Date.now(),
            name: '',
            telefone: '',
            role: mergedContextData.defaultRole || null,
            email: '',
            cpf: '',
            dataNascimento: null,
            password: '',
            confirmPassword: '',
            metadata: {
                tipoUsuario: 'interno',
                notificacoes: { email: true, sms: false, push: true }
            },
            isActive: true,
            status: 'ativo', // Inicia Ativo
            requirePasswordChange: false,
            isFranquiaMode: isFranquiaMode,
            franquiaId: targetFranquiaId,
            account: targetAccountId,
            profissionalId: null,
            unidades: [],
            relationshipId: null,
            profissional: {
                isProfissional: false,
                especialidade: '',
                registroProfissional: '',
                status: 'ativo',
                metadata: { corAgenda: '#3788d8' }
            },
            isNew: true
        };
    }, [mergedContextData.defaultRole, isFranquiaMode, targetFranquiaId, targetAccountId]);

    const handleRefresh = useCallback(async () => {
        if (!mountedRef.current) return;
        isInitializedRef.current = false;
        if (refetchUser) await refetchUser();
        if (refetchAccountUser) await refetchAccountUser();
    }, [refetchUser, refetchAccountUser]);

    useEffect(() => {
        if (!mountedRef.current) return;
        if (isInitializedRef.current && formData) return;
        if (mode === null) return;

        if (mode === 'creating') {
            if (!formData) {
                const newUser = initializeCreationForm();
                setFormData(newUser);
                setLoadingError(null);
                isInitializedRef.current = true;
            }
            return;
        }

        if (mode === 'editing') {
            if (formData && !formData.isNew) {
                isInitializedRef.current = true;
                return;
            }
            
            if (!targetUserId) {
                setLoadingError('ID de usuário inválido');
                isInitializedRef.current = true;
                return;
            }
            
            if (isLoadingUser || isLoadingAccountUser) return;
            
            if (userError) {
                setLoadingError('Erro ao carregar usuário: ' + (userError.message || 'Erro desconhecido'));
                isInitializedRef.current = true;
                return;
            }
            
            if (!userData) return;

            let userRole = userData.role;
            let vinculoAtivo = null;
            let unidadesPermitidas = [];
            
            if (accountUsersData?.data && accountUsersData.data.length > 0) {
                // ✅ CORREÇÃO CRÍTICA AQUI: Remover a trava de "status === ativo" para encontrar inativos na clínica
                vinculoAtivo = accountUsersData.data.find(v => (!targetAccountId || String(v.account?._id || v.account) === String(targetAccountId)));
                
                if (!vinculoAtivo) {
                    vinculoAtivo = accountUsersData.data[0];
                }

                if (vinculoAtivo) {
                    userRole = vinculoAtivo.role?._id || vinculoAtivo.role;
                    if (Array.isArray(vinculoAtivo.unidades)) {
                        unidadesPermitidas = vinculoAtivo.unidades.map(u => u._id || u.id || u);
                    }
                }
            }

            const userIdValue = userData._id || userData.id || targetUserId;
            
            const combinedUser = {
                _id: userIdValue,
                id: userIdValue,
                name: userData.name || '',
                telefone: userData.telefone || '',
                email: userData.email || '',
                cpf: userData.cpf || '',
                dataNascimento: userData.dataNascimento || null,
                role: userRole,
                profissionalId: userData.profissionalId || null,
                isProfissional: !!userData.profissionalId,
                metadata: userData.metadata || {
                    tipoUsuario: 'interno',
                    notificacoes: { email: true, sms: false, push: true }
                },
                isActive: userData.isActive !== false,
                status: vinculoAtivo ? vinculoAtivo.status : 'inativo', // ✅ STATUS LOCAL EXTRAÍDO COM SUCESSO
                requirePasswordChange: userData.requirePasswordChange || false,
                isFranquiaMode: isFranquiaMode,
                franquiaId: targetFranquiaId,
                account: targetAccountId,
                relationshipId: vinculoAtivo?._id || null,
                vinculoAtivo: vinculoAtivo,
                unidades: unidadesPermitidas, 
                photoUrl: userData.photoUrl || userData.photo || '',
                photo: userData.photo || '',
                isNew: false
            };
            
            setFormData(combinedUser);
            setLoadingError(null);
            isInitializedRef.current = true;
        }
    }, [
        mode, formData, initializeCreationForm, targetUserId, isLoadingUser, 
        isLoadingAccountUser, userError, userData, accountUsersData, 
        targetAccountId, targetFranquiaId, isFranquiaMode
    ]);

    useEffect(() => {
        isInitializedRef.current = false;
    }, [mode, userId]);

    const handleFormChange = useCallback((eventOrData) => {
        if (!formData) return;
        
        if (eventOrData?.target) {
            const { name, value, type, checked } = eventOrData.target;
            const finalValue = type === 'checkbox' ? checked : value;
            
            setFormData(prev => ({
                ...prev,
                [name]: finalValue
            }));
            
            if (formErrors[name]) {
                setFormErrors(prev => ({ ...prev, [name]: null }));
            }
        } 
        else if (eventOrData?.target?.name === 'nestedUpdate' && eventOrData?.target?.value) {
            setFormData(eventOrData.target.value);
        }
        else if (eventOrData?.name) {
            setFormData(prev => ({
                ...prev,
                [eventOrData.name]: eventOrData.value
            }));
        }
    }, [formData, formErrors]);

    const handleSavePhoto = useCallback(async (file) => {
        if (!file || !formData?._id) return;
        
        setIsUploadingPhoto(true);
        setFormErrors({});
        
        try {
            const formDataObj = new FormData();
            formDataObj.append('photo', file);

            const { data: result } = await apiClient.put(
                `/users/${formData._id}/photo`,
                formDataObj,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            setFormData(prev => ({
                ...prev,
                photoUrl: result.data?.photoUrl || result.data?.photo,
                photo: result.data?.photo || result.data?.photoUrl
            }));
            
            setAlert('Foto atualizada com sucesso!', 'success');
            
        } catch (error) {
            setAlert(error.message || 'Erro ao enviar foto', 'danger');
        } finally {
            setIsUploadingPhoto(false);
        }
    }, [formData?._id, setAlert]);

    const handleRemovePhoto = useCallback(async () => {
        if (!formData?._id) return;
        setIsUploadingPhoto(true);
        
        try {
            await apiClient.delete(`/users/${formData._id}/photo`);

            setFormData(prev => ({
                ...prev,
                photoUrl: '',
                photo: ''
            }));
            
            setAlert('Foto removida com sucesso!', 'success');
            
        } catch (error) {
            setAlert(error.message || 'Erro ao remover foto', 'danger');
        } finally {
            setIsUploadingPhoto(false);
        }
    }, [formData?._id, setAlert]);

    const validateForm = useCallback(() => {
        if (!formData) return false;
        
        const errors = {};
        
        if (!formData.name?.trim()) errors.name = 'Nome é obrigatório';
        
        if (!formData.telefone?.trim()) {
            errors.telefone = 'Telefone é obrigatório';
        } else {
            const telefoneLimpo = formData.telefone.replace(/\D/g, '');
            if (telefoneLimpo.length < 10 || telefoneLimpo.length > 11) {
                errors.telefone = 'Telefone inválido (deve ter 10 ou 11 dígitos)';
            }
        }
        
        if (!formData.role) errors.role = 'Role é obrigatória';
        
        if (mode === 'creating' && formData.password && formData.password.length < 6) {
            errors.password = 'A senha deve ter no mínimo 6 caracteres';
        }
        
        if (mode === 'creating' && formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'As senhas não coincidem';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, mode]);

    const handleSaveProfile = async () => {
        if (!formData) return;
        if (mode === 'editing' && (!formData._id || formData.isNew)) return;
        if (!validateForm()) {
            setAlert('Corrija os erros no formulário', 'warning');
            return;
        }

        try {
            const telefoneLimpo = formData.telefone.replace(/\D/g, '');

            if (mode === 'creating') {
                const userPayload = {
                    name: formData.name.trim(),
                    telefone: telefoneLimpo,
                    role: formData.role,
                    email: formData.email?.trim()?.toLowerCase() || null,
                    cpf: formData.cpf?.replace(/\D/g, '') || null,
                    dataNascimento: formData.dataNascimento || null,
                    status: formData.status || 'ativo',
                    isActive: formData.isActive !== false,
                    metadata: formData.metadata || { tipoUsuario: 'interno' },
                    requirePasswordChange: formData.requirePasswordChange || false
                };

                if (formData.password?.trim()) {
                    userPayload.password = formData.password;
                }
                
                const createResult = await createUser.mutateAsync(userPayload);
                const newUserId = createResult.data?._id || createResult._id;

                if (targetAccountId) {
                    await createAccountUser.mutateAsync({
                        account: targetAccountId,
                        user: newUserId,
                        role: formData.role,
                        unidades: formData.unidades || [],
                        status: formData.status || 'ativo',
                        criadoPor: contextData?.currentUserId || currentUser?._id || null
                    });
                }

                setAlert('Usuário criado e vinculado com sucesso!', 'success');
                setTimeout(() => navigate(-1), 1500);
            }
            else if (mode === 'editing') {
                const updatePayload = {
                    name: formData.name.trim(),
                    telefone: telefoneLimpo,
                    email: formData.email?.trim()?.toLowerCase() || null,
                    cpf: formData.cpf?.replace(/\D/g, '') || null,
                    dataNascimento: formData.dataNascimento || null,
                    role: formData.role,
                    isActive: formData.isActive,
                    status: formData.status, // ✅ INJEÇÃO DO STATUS DO VÍNCULO
                    accountId: targetAccountId, // ✅ ENVIA O ID DA CONTA PARA SABER QUAL VÍNCULO ATUALIZAR
                    metadata: formData.metadata,
                    requirePasswordChange: formData.requirePasswordChange || false
                };
                
                await updateUser.mutateAsync({ 
                    userId: String(formData._id).trim(), 
                    userData: updatePayload 
                });

                setAlert('Usuário atualizado com sucesso!', 'success');
                await handleRefresh();
            }

        } catch (error) {
            let errorMessage = 'Erro ao processar';
            if (error.response?.data?.error) errorMessage = error.response.data.error;
            else if (error.response?.data?.message) errorMessage = error.response.data.message;
            else if (error.message) errorMessage = error.message;
            
            if (errorMessage.includes('duplicate key') || errorMessage.includes('duplicate')) {
                if (errorMessage.includes('telefone')) errorMessage = 'Este telefone já está cadastrado';
                else if (errorMessage.includes('email')) errorMessage = 'Este email já está cadastrado';
            }
            setAlert(errorMessage, 'danger');
        }
    };

    const profissionalData = useMemo(() => {
        if (formData?.profissionalId) return { id: formData.profissionalId };
        return {};
    }, [formData?.profissionalId]);

    const isLoading = useMemo(() => {
        if (mode === null) return true;
        if (mode === 'creating') return false;
        if (mode === 'editing') {
            return (isLoadingUser || isLoadingAccountUser) && !formData && !loadingError;
        }
        return false;
    }, [mode, isLoadingUser, isLoadingAccountUser, formData, loadingError]);

    if (isLoading || mode === null) {
        return (
            <div className="page-management-container">
                <div className="table-loading-state">
                    <Loader2 size={24} className="animate-spin" />
                    <span>{mode === null ? 'Determinando modo...' : 'Carregando dados do usuário...'}</span>
                </div>
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="page-management-container">
                <div className="content-panel">
                    <div className="text-center py-12">
                        <AlertCircle className="text-red-500 mb-4 mx-auto" size={48} />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar</h3>
                        <p className="text-gray-600 mb-4">{loadingError}</p>
                        <button onClick={() => navigate(-1)} className="action-button action-button-secondary">
                            <XCircle size={16} className="icon" /> Fechar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!formData) return null;

    return (
        <div className="page-management-container">
            <PageHeader
                icon={<User className="icon-title" />}
                title={mode === 'creating' ? 'Novo Usuário' : (formData?.name || 'Editar Usuário')}
                subtitle={null}
                actions={
                    <button type="button" onClick={() => navigate(-1)} className="action-button action-button-secondary">
                        <XCircle size={18} className="icon" /> Fechar
                    </button>
                }
            />

            <div className="content-panel">
                <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

                <div className="p-6 min-h-[400px]">
                    {currentStep === 1 && (
                        <UserProfileTab
                            formData={formData}
                            onChange={handleFormChange}
                            context={mergedContextData}
                            isEditing={mode === 'editing' && canEdit}
                            isCreating={mode === 'creating'}
                            isCurrentUser={isOwnUser}
                            loggedInUserId={currentUser?._id}
                            userToEdit={formData}
                            isLoading={isLoading}
                            isUploadingPhoto={isUploadingPhoto}
                            formErrors={formErrors}
                            onSavePhoto={handleSavePhoto}
                            onRemovePhoto={handleRemovePhoto}
                            canManagePhoto={canEdit}
                            isOwnProfile={isOwnUser}
                            isMatrizAdmin={isMatrizAdmin(currentUser)}
                            profissionalData={profissionalData}
                            onSave={handleSaveProfile} 
                            isSaving={loading}
                        />
                    )}
                    
                    {currentStep === 2 && (
                        <PermissoesUsuarioTab
                            user={formData}
                            contextData={{
                                ...mergedContextData,
                                contaId: targetAccountId,
                                franquiaId: targetFranquiaId
                            }}
                            onUpdateSuccess={handleRefresh}
                            onChange={handleFormChange}
                            isCreating={mode === 'creating'}
                        />
                    )}
                    
                    {currentStep === 3 && (
                        <UserPasswordTab
                            formData={formData}
                            onChange={handleFormChange}
                            isEditing={mode === 'editing'}
                            formErrors={formErrors}
                        />
                    )}
                </div>

                {canEdit ? (
                    <FormStepNavigation
                        currentStep={currentStep}
                        totalSteps={STEP_LABELS.length}
                        onBack={() => setCurrentStep(s => s - 1)}
                        onNext={() => setCurrentStep(s => s + 1)}
                        onSubmit={handleSaveProfile}
                        onCancel={() => navigate(-1)}
                        isSubmitting={loading}
                        submitLabel={mode === 'creating' ? 'Criar Usuário' : 'Salvar Alterações'}
                    />
                ) : (
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="action-button action-button-secondary"
                        >
                            <XCircle size={18} className="icon" /> Fechar
                        </button>
                    </div>
                )}
            </div>

            {mode === 'editing' && !canEdit && !isOwnUser && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-3">
                        <Lock className="text-yellow-600 mt-0.5" size={20} />
                        <div>
                            <h4 className="text-sm font-medium text-yellow-800">Modo Visualização</h4>
                            <p className="text-sm text-yellow-700 mt-1">
                                Você está visualizando este usuário. Apenas administradores podem editar outros usuários.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagementDrawer;